import { useEffect, useRef } from "react"
import { io, Socket } from "socket.io-client"
import { useAuthStore } from "@/store/auth.store"
import { useNotificationStore } from "@/store/notification.store"
import { toast } from "sonner"
import { getUnreadCount } from "@/lib/api/notification.api"

interface NotificationProviderProps {
  children: React.ReactNode
}

export function NotificationProvider({ children }: NotificationProviderProps) {
  const accessToken = useAuthStore((s) => s.accessToken)
  const addNotification = useNotificationStore((s) => s.addNotification)
  const socketRef = useRef<Socket | null>(null)

  useEffect(() => {
    if (!accessToken) {
      if (socketRef.current) {
        socketRef.current.disconnect()
        socketRef.current = null
      }
      return
    }

    const apiUrl = import.meta.env.VITE_API_URL || window.location.origin

    // Connect to the default root namespace
    const socket = io(`${apiUrl}/notifications`, {
      auth: {
        token: `Bearer ${accessToken}`,
      },
      transports: ["websocket"],
    })
    socketRef.current = socket

    socket.on("connect", () => {
      console.log("WebSocket connected to notifications namespace")
    })

    socket.on("notification", (data: { title: string; body: string; type?: string; metadata?: any }) => {
      console.log("Notification received:", data)
      try {
        // Save to store
        addNotification({
          title: data.title,
          body: data.body,
          type: data.type,
          metadata: data.metadata,
        })

        // Fetch updated unread count from server
        getUnreadCount(accessToken).then((res) => {
          if (res.success) {
            useNotificationStore.getState().setUnreadCount(res.data)
          }
        }).catch((err) => {
          console.error("Error fetching unread count on socket notification:", err)
        })

        // Trigger visual toast
        toast.info(data.title || "Notification Received", {
          description: data.body,
          duration: 5000,
        })
      } catch (err) {
        console.error("Error in socket notification handler:", err)
      }
    })

    socket.on("disconnect", (reason) => {
      console.log("WebSocket disconnected:", reason)
    })

    socket.on("connect_error", (error) => {
      console.error("WebSocket connection error:", error)
    })

    return () => {
      socket.disconnect()
      socketRef.current = null
    }
  }, [accessToken, addNotification])

  return <>{children}</>
}
